/**
 * ImageGeneratePanel - AI Image Generator panel for the PowerEditor
 * Layout: Header > (Generate Form + Gallery) OR (Edit Panel when isEdit=true)
 */

import { useState, useMemo } from "react";

import {
  Search,
  RefreshCw,
  Trash2,
  ListChecks,
  ChevronLeft,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ImageGallery } from "./image-gallery";
import { ImageGeneratorForm } from "./image-generator-form";
import { GeneratingImageCard } from "./generating-image-card";
import { useImageGeneration } from "./use-image-generation";
import { ImageGeneratePanelProps } from "@/types/image-generate-panel.types";
import { cn } from "@/lib/utils";
import { useResponsive } from "@/hooks/useResponsive";
import { ImageEditorTabContent } from "./image-editor-tab-content";

function Header({ onBack }: { onBack: () => void }) {
  return (
    <div className="h-12 px-4 border-b border-border flex items-center justify-between bg-background">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-muted-foreground transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <span>Image Generator</span>
      </button>
    </div>
  );
}

export default function ImageGeneratePanel({
  projectId,
  devContainerId,
  previewUrl,
  onBack,
}: ImageGeneratePanelProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const {
    // Gallery state
    generatedImages,
    isLoadingAssets,

    // Pagination
    hasMoreGeneratedImages,
    isLoadingMoreGeneratedImages,
    loadMoreGeneratedImages,

    // Selection state
    selectedAssets,
    toggleSelection,
    clearSelection,
    selectAll,
    isDeleting,
    handleDeleteSelected,

    // Editor state
    editingImage,
    isEdit,
    openImageEditor,
    closeImageEditor,
    handleImageUpdated,

    // Generation state
    generatingImages,
    isGenerating,
    prompt,
    setPrompt,
    filename,
    setFilename,
    aspectRatio,
    setAspectRatio,
    stylePreset,
    setStylePreset,
    handleGenerate,
    removeGeneratingImage,

    // Reference images
    referenceImages,
    fileInputRef,
    handleReferenceImageUpload,
    removeReferenceImage,

    // Computed values
    selectedImagesForEdit,
  } = useImageGeneration({ projectId, devContainerId, previewUrl });

  const { isMobile } = useResponsive();

  // Filter images by search query
  const filteredImages = useMemo(() => {
    if (!searchQuery.trim()) return generatedImages;
    const query = searchQuery.toLowerCase();
    return generatedImages.filter(
      (img) =>
        img.expectedFilename.toLowerCase().includes(query) ||
        img.prompt.toLowerCase().includes(query),
    );
  }, [generatedImages, searchQuery]);

  const hasImages = generatedImages.length > 0;
  const hasFilteredImages = filteredImages.length > 0;
  const hasGenerating = generatingImages.length > 0;

  return (
    <div
      className={cn("flex flex-col h-full", isMobile ? "" : " bg-background")}
    >
      {/* Header */}
      <Header onBack={isEdit ? closeImageEditor : onBack} />

      {/* Conditional Rendering: Generate View OR Edit View */}
      {!isEdit ? (
        <>
          {/* Generate View */}
          {/* Form section - fixed at top */}
          <div className="p-4">
            <ImageGeneratorForm
              prompt={prompt}
              onPromptChange={setPrompt}
              filename={filename}
              onFilenameChange={setFilename}
              aspectRatio={aspectRatio}
              onAspectRatioChange={setAspectRatio}
              stylePreset={stylePreset}
              onStylePresetChange={setStylePreset}
              referenceImages={referenceImages}
              fileInputRef={fileInputRef}
              onReferenceImageUpload={handleReferenceImageUpload}
              onRemoveReferenceImage={removeReferenceImage}
              selectedImagesForEdit={selectedImagesForEdit}
              onClearSelection={clearSelection}
              onToggleSelection={toggleSelection}
              isGenerating={isGenerating}
              onGenerate={handleGenerate}
            />
          </div>

          {/* Gallery + generating - fills remaining space */}
          <ScrollArea className="flex-1 bg-muted/40">
            {/* Generating Images Progress */}
            {hasGenerating && (
              <div className="px-4 py-3 border-b border-border/50">
                <p className="text-[10px] font-medium text-muted-foreground mb-1.5">
                  Generating ({generatingImages.length})
                </p>
                <div className="space-y-1 max-h-[150px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                  {generatingImages.map((img) => (
                    <GeneratingImageCard
                      key={img.requestId}
                      image={img}
                      onRemove={removeGeneratingImage}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Gallery Section */}
            <div className="px-4 py-3">
              {/* Gallery header with search toggle (only when images exist) */}
              {hasImages &&
                (() => {
                  // Derive tri-state of the "Select all" toggle so users get
                  // immediate visual feedback when they click it.
                  const visibleIds = filteredImages.map((img) => img.id);
                  const visibleSelected = visibleIds.filter((id) =>
                    selectedAssets.has(id),
                  ).length;
                  const allSelected =
                    visibleIds.length > 0 &&
                    visibleSelected === visibleIds.length;
                  const someSelected = visibleSelected > 0 && !allSelected;

                  return (
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        {generatedImages.length} image
                        {generatedImages.length !== 1 ? "s" : ""}
                        {selectedAssets.size > 0 && (
                          <span className="ml-1.5 text-primary normal-case tracking-normal">
                            · {selectedAssets.size} selected
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "h-7 w-7 p-0 rounded-full",
                            showSearch && "bg-muted text-foreground",
                          )}
                          onClick={() => {
                            setShowSearch(!showSearch);
                            if (showSearch) setSearchQuery("");
                          }}
                          title="Search images"
                        >
                          <Search className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant={allSelected ? "default" : "ghost"}
                          size="sm"
                          className={cn(
                            "h-7 px-2.5 text-xs gap-1.5 rounded-full transition-colors",
                            someSelected &&
                              "bg-primary/10 text-primary hover:bg-primary/15",
                          )}
                          onClick={allSelected ? clearSelection : selectAll}
                          title={allSelected ? "Deselect all" : "Select all"}
                        >
                          {!allSelected && !someSelected && (
                            <ListChecks className="w-3.5 h-3.5" />
                          )}
                          {allSelected
                            ? "Deselect all"
                            : someSelected
                              ? `${visibleSelected} selected`
                              : "Select all"}
                        </Button>
                      </div>
                    </div>
                  );
                })()}

              {/* Inline search bar */}
              {showSearch && hasImages && (
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search images..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 pr-8 h-8 text-sm bg-background focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted text-muted-foreground transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}

              <ImageGallery
                images={filteredImages}
                selectedAssets={selectedAssets}
                isLoading={isLoadingAssets}
                onImageClick={(img) => toggleSelection(img.id)}
                onEditImage={openImageEditor}
                // Pagination is server-side; disable when the user is
                // filtering client-side so we don't accidentally fetch
                // the next page while the filter hides nearly everything.
                hasMore={!searchQuery.trim() && hasMoreGeneratedImages}
                isLoadingMore={isLoadingMoreGeneratedImages}
                onLoadMore={loadMoreGeneratedImages}
              />

              {/* No results after filter */}
              {!hasFilteredImages && hasImages && searchQuery && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Search className="w-5 h-5 text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No images match "{searchQuery}"
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Selection Footer - only when items are selected */}
          {selectedAssets.size > 0 && (
            <div className="px-4 py-2.5 border-t border-border flex items-center justify-between bg-muted/50">
              <span className="text-xs text-muted-foreground">
                {selectedAssets.size} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  onClick={clearSelection}
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                >
                  Clear
                </Button>
                <Button
                  onClick={handleDeleteSelected}
                  variant="destructive"
                  size="sm"
                  disabled={isDeleting}
                  className="h-7 text-xs"
                >
                  {isDeleting ? (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete ({selectedAssets.size})
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Edit View */
        <ScrollArea className="flex-1">
          {editingImage && projectId && (
            <ImageEditorTabContent
              image={editingImage}
              projectId={projectId}
              devContainerId={devContainerId}
              previewUrl={previewUrl}
              onImageUpdated={handleImageUpdated}
            />
          )}
        </ScrollArea>
      )}
    </div>
  );
}
